import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import UploadOutlined from '@mui/icons-material/UploadOutlined';
import { Button, FormSection } from '../../../../../components/ui';
import { useSnackBar } from '../../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import QuickTemplateModal from '../../../../../components/Leads/QuickTemplateModal.tsx';
import templateAssetService from '../../services/templateAssetService';
import { spacing } from '../../../../../theme/tokens/spacing.js';

function SaveFirstNotice() {
  return (
    <Typography color="text.secondary">
      Save the form first to attach templates and help files.
    </Typography>
  );
}

export default function AssetsSection({ studio, formId, hasFormId }) {
  const { showSnackBar } = useSnackBar();
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpDetailDialogOpen, setHelpDetailDialogOpen] = useState(false);
  const [helpFiles, setHelpFiles] = useState([]);
  const [helpFileDetails, setHelpFileDetails] = useState(null);
  const [helpFileDetailLoading, setHelpFileDetailLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [helpFile, setHelpFile] = useState(null);
  const [helpFileName, setHelpFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);
  const helpFileInputRef = useRef(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const templates = studio.form?.templates || [];

  const loadHelpFiles = useCallback(async () => {
    if (!hasFormId) return;
    try {
      const response = await templateAssetService.listHelpFiles(formId);
      setHelpFiles(Array.isArray(response?.data) ? response.data : []);
    } catch {
      setHelpFiles([]);
    }
  }, [formId, hasFormId]);

  useEffect(() => {
    loadHelpFiles();
  }, [loadHelpFiles]);

  if (!hasFormId) return <SaveFirstNotice />;

  const handleUploadFromPicker = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size === 0) {
      setFileError('File cannot be empty (0MB).');
      showSnackBar('Empty file (0MB) cannot be uploaded.', 'warning');
      setHelpFile(null);
      setHelpFileName('');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 5MB. Allowed only up to 5MB.');
      showSnackBar('File size exceeds 5MB. Allowed only up to 5MB.', 'warning');
      setHelpFile(null);
      setHelpFileName('');
      event.target.value = '';
      return;
    }

    setFileError('');
    setHelpFile(file || null);
    setHelpFileName(file?.name || '');
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (!helpFile) {
      showSnackBar('Please choose a .xlsx/.xls file.', 'warning');
      return;
    }

    if (helpFile.size === 0) {
      setFileError('File cannot be empty (0MB).');
      showSnackBar('Empty file (0MB) cannot be uploaded.', 'warning');
      return;
    }

    if (helpFile.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 5MB. Allowed only up to 5MB.');
      showSnackBar('File size exceeds 5MB. Allowed only up to 5MB.', 'warning');
      return;
    }

    setIsUploading(true);
    try {
      const response = await templateAssetService.uploadHelpFile(
        formId,
        helpFile,
        helpFileName || helpFile.name || 'help file'
      );
      if (response?.status === 200) {
        showSnackBar('Help file uploaded', 'success');
        setHelpDialogOpen(false);
        setHelpFile(null);
        setHelpFileName('');
        setFileError('');
        loadHelpFiles();
      } else {
        showSnackBar('Upload failed', 'error');
      }
    } catch {
      showSnackBar('Upload failed', 'error');
    } finally {
      setIsUploading(false);
      if (helpFileInputRef.current) {
        helpFileInputRef.current.value = '';
      }
    }
  };

  const openHelpFileDetails = async (file) => {
    const fileId = file?._id || file?.id || '';
    if (!fileId) return;

    setHelpFileDetailLoading(true);
    try {
      const response = await templateAssetService.getHelpFileDetails(fileId);
      setHelpFileDetails(response?.data || response || null);
      setHelpDetailDialogOpen(true);
    } catch {
      showSnackBar('Unable to load help file details.', 'error');
    } finally {
      setHelpFileDetailLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <FormSection title="Quick templates" description="Attach approved WhatsApp templates to this form.">
        <Stack direction="row" spacing={0} flexWrap="wrap" sx={{ mb: 2 , gap: spacing.size1 , flexFlow:{xs:'column',sm:'wrap'} , maxHeight:{xs:'15vh' , sm:'15hv'},overflowY:{xs:'auto',sm:'auto'},
        '&::-webkit-scrollbar': {
            height: {
              xs: '4px',
              sm: 0,
            },
          },
        }}>
          {templates.map((template) => (
            <Chip key={template.md_id || template.name} label={template.name} />
          ))}
          {!templates.length ? (
            <Typography color="text.secondary">No templates selected.</Typography>
          ) : null}
        </Stack>
        <Button variant="outline" onClick={() => setTemplateModalOpen(true)}>
          Manage templates
        </Button>
        <QuickTemplateModal
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          quickTemplates={templates}
          setQuickTemplates={(nextTemplates) => studio.updateTemplates(nextTemplates)}
          onSubmit={(nextTemplates) => {
            studio.updateTemplates(nextTemplates);
            setTemplateModalOpen(false);
          }}
        />
      </FormSection>

      <FormSection title="Help files" description="Upload Excel help files used by agents for this form.">
        <Box sx={{ mb: 2 }}>
          <Button
            variant="primary"
            icon={<UploadOutlined />}
            disabled={isUploading}
            onClick={() => {
              setHelpFile(null);
              setHelpFileName('');
              setFileError('');
              setHelpDialogOpen(true);
            }}
          >
            Upload help file
          </Button>
        </Box>
        <Stack spacing={1}>
          {helpFiles.map((file) => (
            <Box
              key={file._id || file.file_name}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
              }}
              onClick={() => openHelpFileDetails(file)}
            >
              <Typography fontWeight={600}>{file.file_name || 'Help file'}</Typography>
            </Box>
          ))}
          {!helpFiles.length ? (
            <Typography color="text.secondary">No help files uploaded yet.</Typography>
          ) : null}
        </Stack>
      </FormSection>

      <Dialog open={helpDetailDialogOpen} onClose={() => setHelpDetailDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Help File Records</DialogTitle>
        <DialogContent>
          {helpFileDetailLoading ? (
            <Typography color="text.secondary">Loading details...</Typography>
          ) : helpFileDetails?.records?.length ? (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(helpFileDetails.available_columns || []).map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700 }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(helpFileDetails.records || []).map((record, idx) => (
                    <TableRow key={idx}>
                      {(helpFileDetails.available_columns || []).map((col) => (
                        <TableCell key={col}>{record?.[col] ?? '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No records found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={() => setHelpDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={helpDialogOpen}
        onClose={() => {
          setHelpDialogOpen(false);
          setHelpFile(null);
          setHelpFileName('');
          setFileError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Help File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Help File Name"
              value={helpFileName}
              onChange={(event) => setHelpFileName(event.target.value)}
              sx={{ mb: 2 }}
            />

            <input
              ref={helpFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleUploadFromPicker}
            />

            <Box display="flex" gap={1} alignItems="center">
              <Button variant="outline" onClick={() => helpFileInputRef.current?.click()}>
                {helpFile ? 'Change File' : 'Choose File'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {helpFile ? helpFile.name : 'No file chosen'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Accepted: .xlsx, .xls
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Allowed only 5MB
            </Typography>
            {fileError ? (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {fileError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline"
            onClick={() => {
              setHelpDialogOpen(false);
              setHelpFile(null);
              setHelpFileName('');
              setFileError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isUploading || !helpFile || Boolean(fileError)}
            onClick={handleUpload}
          >
            {isUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
